/**
 * Sentry error helpers. Can be imported in any component.
 */
import {
    withScope,
    captureException,
    setUser,
    addBreadcrumb,
    captureMessage,
} from "@sentry/vue";
import type { SeverityLevel } from "@sentry/vue";

/**
 * Configures the Sentry error structure
 *
 * @param {string} componentName - component where the error is triggered from
 * @param {string} method - method/function where the error is triggered from
 * @param {any} error - the triggered error (any: string | Error)
 */
export const configSentryError = (
    componentName: string,
    method: string,
    error: any,
) => {
    const transactionName = `${componentName} | ${method}`;
    withScope((scope) => {
        scope.setTransactionName(transactionName);

        /** If it's already an Error then capture it directly */
        captureException(error instanceof Error ? error : new Error(error));
    });
};

/**
 * Configures the Sentry error structure
 *
 * @param {string} transactionName - component/method where the error is triggered from
 * @param {any} error - the error to report (any: string | Error)
 */
export const configSentryApiError = (transactionName: string, error: any) => {
    withScope(function (scope) {
        scope.setTransactionName(transactionName);
        captureException(error instanceof Error ? error : new Error(error));
    });
};

/**
 * Sets the user's identity
 *
 * @param {string} emailAddress
 * @param {string} sessionID - ties the user to a session
 */
export const setSentryUser = (emailAddress: string, sessionID: string) => {
    setUser({
        id: sessionID,
        email: emailAddress,
    });
};

/**
 * Manually adds a breadcrumb in the Sentry event.
 * Breadcrumb config - https://develop.sentry.dev/sdk/event-payloads/breadcrumbs/
 *
 * @param {string} category - "started", "sentry.transaction", "ui.click", "click", "navigation", "error", "xhr"
 * @param {string} message - the breadcrumb message
 * @param {SeverityLevel} level - "fatal", "critical", "error", "warning", "log", "info", and "debug".
 */
export const createSentryBreadcrumb = (
    category: string,
    message: string,
    level: SeverityLevel,
) => {
    addBreadcrumb({
        category: category,
        message: message,
        level: level,
    });
};

export const captureSentryMessage = (message: string) => {
    withScope(function (scope) {
        scope.setLevel("info");
        // The exception has the event level set by the scope (info).
        captureMessage(message, "info");
    });
};
